import { useState, useEffect, useCallback } from "react";
import type { SyncFinding, FindingsSummary } from "../api";
import api from "../api";
import { exportFindingsToExcel } from "../utils/findings-export";

// ─── Findings Panel ─────────────────────────────────────────────
// Shows quality findings from sync quality checks.
// Grouped by severity with resolve action and category filters.
// "Fix it" calls the pipeline directly. "Fix All" processes all unresolved findings.

interface Props {
  projectId: string;
  /** Called when user clicks "Fix it" — passes a pre-built prompt for the chat */
  onFixIt?: (prompt: string) => void;
}

const SEVERITY_ORDER = ["critical", "high", "medium", "low", "info"] as const;
const SEVERITY_CONFIG: Record<string, { label: string; bg: string; text: string; border: string; icon: string }> = {
  critical: { label: "Critical", bg: "rgba(239, 68, 68, 0.08)", text: "#ef4444", border: "#ef4444", icon: "🔴" },
  high: { label: "High", bg: "rgba(0, 229, 255, 0.08)", text: "#00E5FF", border: "#00E5FF", icon: "🟠" },
  medium: { label: "Medium", bg: "rgba(234, 179, 8, 0.08)", text: "#eab308", border: "#eab308", icon: "🟡" },
  low: { label: "Low", bg: "rgba(59, 130, 246, 0.08)", text: "#3b82f6", border: "#3b82f6", icon: "🔵" },
  info: { label: "Info", bg: "rgba(107, 114, 128, 0.08)", text: "#6b7280", border: "#6b7280", icon: "⚪" },
};

const CATEGORY_LABELS: Record<string, string> = {
  ci: "CI/CD",
  testing: "Testing",
  documentation: "Documentation",
  security: "Security",
  quality: "Quality",
  configuration: "Configuration",
};

const COVERAGE_PATTERNS = [/test coverage/i, /coverage.*regress/i, /low.*coverage/i, /no test framework/i];

function isCoverageFinding(finding: SyncFinding): boolean {
  return COVERAGE_PATTERNS.some((p: RegExp) => p.test(finding.title));
}

function estimateFixCredits(finding: SyncFinding): number {
  if (finding.category === 'ci') return 5;
  if (finding.category === 'documentation') return 3;
  if (finding.category === 'configuration') return 2;
  if (finding.category === 'security') return 10;
  return 5;
}

function fixDescription(finding: SyncFinding): string {
  if (finding.category === 'ci') return 'Add or update the CI/CD workflow configuration.';
  if (finding.category === 'documentation') return `Update ${finding.file_path || 'project documentation'}.`;
  if (finding.category === 'security') return 'Apply security fix and update affected files.';
  if (finding.suggestion) return finding.suggestion;
  return 'Apply the suggested fix to the affected file(s).';
}

export default function FindingsPanel({ projectId, onFixIt }: Props) {
  const [findings, setFindings] = useState<SyncFinding[]>([]);
  const [summary, setSummary] = useState<FindingsSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<"all" | "unresolved">("unresolved");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [engineFilter, setEngineFilter] = useState<"all" | "manus" | "gemini_opus">("all");
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [resolving, setResolving] = useState<Set<string>>(new Set());
  const [fixing, setFixing] = useState<Set<string>>(new Set());
  const [fixingAll, setFixingAll] = useState(false);
  const [confirmFinding, setConfirmFinding] = useState<SyncFinding | null>(null);

  const loadFindings = useCallback(async () => {
    try {
      const findingsApi = (api as any).findings as typeof import("../api")["default"] & {
        list: (id: string, opts?: { unresolved?: boolean }) => Promise<SyncFinding[]>;
        summary: (id: string) => Promise<FindingsSummary>;
      };
      const [findingsList, findingsSummary] = await Promise.all([
        findingsApi.list(projectId, { unresolved: filter === "unresolved" }),
        findingsApi.summary(projectId),
      ]);
      setFindings(findingsList);
      setSummary(findingsSummary);
    } catch (err) {
      console.error("Failed to load findings:", err);
    } finally {
      setLoading(false);
    }
  }, [projectId, filter]);

  useEffect(() => {
    loadFindings();
  }, [loadFindings]);

  const handleResolve = async (findingId: string) => {
    setResolving(prev => new Set(prev).add(findingId));
    try {
      const findingsApi = (api as any).findings as any;
      await findingsApi.resolve(findingId);
      await loadFindings();
    } catch (err) {
      console.error("Failed to resolve finding:", err);
    } finally {
      setResolving(prev => {
        const next = new Set(prev);
        next.delete(findingId);
        return next;
      });
    }
  };

  const handleFixIt = async (finding: SyncFinding) => {
    setFixing(prev => new Set(prev).add(finding.id));
    try {
      const result = await api.chatV2FixFinding(projectId, finding.id);
      console.log('[FindingsPanel] Fix started:', result);
      // Optionally navigate to chat to watch progress
      if (onFixIt) {
        onFixIt(
          `__FIX_ALL__${result.session_id}__` +
          `Fixing: **${finding.title}** (${finding.severity})`
        );
      }
    } catch (err) {
      console.error("Failed to start fix:", err);
      // Fall back to the old behavior — send to chat
      if (onFixIt) {
        const prompt = `Fix this ${finding.severity} ${finding.category} finding: "${finding.title}".${finding.file_path ? ` File: ${finding.file_path}.` : ''}${finding.description ? ` Details: ${finding.description}` : ''}${finding.suggestion ? ` Suggestion: ${finding.suggestion}` : ''}`;
        onFixIt(prompt);
      }
    } finally {
      setFixing(prev => {
        const next = new Set(prev);
        next.delete(finding.id);
        return next;
      });
    }
  };

  const handleFixAll = async () => {
    setFixingAll(true);
    try {
      const result = await api.chatV2FixAll(projectId);
      console.log('[FindingsPanel] Fix All started:', result);
      if (onFixIt) {
        // Build a user-friendly message with the findings list
        const findingsList = result.findings
          .map((f, i) => `${i + 1}. **[${f.severity.toUpperCase()}]** ${f.title}`)
          .join('\n');
        onFixIt(
          `__FIX_ALL__${result.session_id}__` +
          `Fix All: Processing ${result.total_findings} findings\n\n${findingsList}`
        );
      }
    } catch (err) {
      console.error("Failed to start Fix All:", err);
    } finally {
      setFixingAll(false);
    }
  };

  const toggle = (id: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const filteredFindings = findings
    .filter((f: SyncFinding) => categoryFilter === "all" || f.category === categoryFilter)
    .filter((f: SyncFinding) => engineFilter === "all" || f.review_engine === engineFilter);

  const grouped = SEVERITY_ORDER.map(sev => ({
    severity: sev,
    items: filteredFindings.filter(f => f.severity === sev),
  })).filter(g => g.items.length > 0);

  if (loading) {
    return (
      <div style={{ padding: "2rem", textAlign: "center", color: "var(--text-muted)" }}>
        Loading findings...
      </div>
    );
  }

  const unresolvedCount = findings.filter(f => !f.resolved).length;

  return (
    <div style={{ padding: "1.5rem", maxWidth: 1100, margin: "0 auto" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1.5rem", flexWrap: "wrap", gap: 12 }}>
        <h2 style={{ fontSize: "var(--text-lg)", fontWeight: 600, display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 20 }}>📋</span> Quality Findings
        </h2>
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          {summary && (
            <div style={{
              display: "flex", alignItems: "center", gap: 12,
              background: "var(--bg-card)", borderRadius: 8, padding: "0.5rem 1rem",
              border: "1px solid var(--border)",
            }}>
              <span style={{ fontSize: "var(--text-sm)", color: "var(--text-muted)" }}>
                {summary.unresolved} unresolved
              </span>
              {summary.critical > 0 && (
                <span style={{ color: "#ef4444", fontWeight: 600, fontSize: "var(--text-sm)" }}>
                  {summary.critical} critical
                </span>
              )}
              {summary.high > 0 && (
                <span style={{ color: "#00E5FF", fontWeight: 600, fontSize: "var(--text-sm)" }}>
                  {summary.high} high
                </span>
              )}
            </div>
          )}
          {unresolvedCount > 0 && (
            <button
              onClick={handleFixAll}
              disabled={fixingAll}
              style={{
                padding: "0.5rem 1rem",
                borderRadius: 8,
                border: "none",
                background: fixingAll ? "var(--text-muted)" : "linear-gradient(135deg, #00E5FF, #ef4444)",
                color: "#fff",
                cursor: fixingAll ? "wait" : "pointer",
                fontSize: "var(--text-sm)",
                fontWeight: 700,
                display: "flex",
                alignItems: "center",
                gap: 6,
                transition: "opacity 0.15s",
                opacity: fixingAll ? 0.7 : 1,
              }}
              onMouseEnter={(e) => !fixingAll && (e.currentTarget.style.opacity = '0.85')}
              onMouseLeave={(e) => !fixingAll && (e.currentTarget.style.opacity = '1')}
            >
              {fixingAll ? "⏳ Starting..." : `🛠️ Fix All (${unresolvedCount})`}
            </button>
          )}
          <button
            onClick={() => exportFindingsToExcel(filteredFindings)}
            disabled={filteredFindings.length === 0}
            title="Export the currently filtered findings to .xlsx"
            style={{
              padding: "0.5rem 1rem",
              borderRadius: 8,
              border: "1px solid var(--border)",
              background: filteredFindings.length === 0 ? "var(--bg-card)" : "transparent",
              color: filteredFindings.length === 0 ? "var(--text-muted)" : "var(--text)",
              cursor: filteredFindings.length === 0 ? "not-allowed" : "pointer",
              fontSize: "var(--text-sm)",
              fontWeight: 600,
              display: "flex",
              alignItems: "center",
              gap: 6,
              opacity: filteredFindings.length === 0 ? 0.6 : 1,
            }}
          >
            ⬇️ Export to Excel ({filteredFindings.length})
          </button>
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: "flex", gap: 8, marginBottom: "1.5rem", flexWrap: "wrap" }}>
        <button
          onClick={() => setFilter("unresolved")}
          style={{
            padding: "0.375rem 0.75rem",
            borderRadius: 6,
            border: `1px solid ${filter === "unresolved" ? "var(--accent)" : "var(--border)"}`,
            background: filter === "unresolved" ? "var(--accent)" : "transparent",
            color: filter === "unresolved" ? "#fff" : "var(--text-muted)",
            cursor: "pointer",
            fontSize: "var(--text-sm)",
          }}
        >
          Unresolved
        </button>
        <button
          onClick={() => setFilter("all")}
          style={{
            padding: "0.375rem 0.75rem",
            borderRadius: 6,
            border: `1px solid ${filter === "all" ? "var(--accent)" : "var(--border)"}`,
            background: filter === "all" ? "var(--accent)" : "transparent",
            color: filter === "all" ? "#fff" : "var(--text-muted)",
            cursor: "pointer",
            fontSize: "var(--text-sm)",
          }}
        >
          All
        </button>
        <span style={{ width: 1, background: "var(--border)", margin: "0 4px" }} />
        {["all", "ci", "testing", "documentation", "security", "quality", "configuration"].map(cat => (
          <button
            key={cat}
            onClick={() => setCategoryFilter(cat)}
            style={{
              padding: "0.375rem 0.75rem",
              borderRadius: 6,
              border: `1px solid ${categoryFilter === cat ? "var(--accent)" : "var(--border)"}`,
              background: categoryFilter === cat ? "var(--accent)" : "transparent",
              color: categoryFilter === cat ? "#fff" : "var(--text-muted)",
              cursor: "pointer",
              fontSize: "var(--text-sm)",
            }}
          >
            {cat === "all" ? "All Categories" : CATEGORY_LABELS[cat] ?? cat}
          </button>
        ))}
        <span style={{ width: 1, background: "var(--border)", margin: "0 4px" }} />
        {(["all", "manus", "gemini_opus"] as const).map(eng => (
          <button
            key={eng}
            onClick={() => setEngineFilter(eng)}
            style={{
              padding: "0.375rem 0.75rem",
              borderRadius: 6,
              border: `1px solid ${engineFilter === eng ? "var(--accent)" : "var(--border)"}`,
              background: engineFilter === eng ? "var(--accent)" : "transparent",
              color: engineFilter === eng ? "#fff" : "var(--text-muted)",
              cursor: "pointer",
              fontSize: "var(--text-sm)",
            }}
          >
            {eng === "all" ? "All Engines" : eng === "manus" ? "Deep Review" : "Quick Review"}
          </button>
        ))}
      </div>

      {/* Empty state */}
      {grouped.length === 0 && (
        <div style={{
          textAlign: "center",
          padding: "3rem",
          color: "var(--text-muted)",
          background: "var(--bg-card)",
          borderRadius: 12,
          border: "1px solid var(--border)",
        }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>✅</div>
          <p style={{ fontSize: "var(--text-base)" }}>
            {filter === "unresolved" ? "No unresolved findings" : "No findings yet"}
          </p>
          <p style={{ fontSize: "var(--text-sm)", marginTop: 4 }}>
            Quality checks run automatically during sync.
          </p>
        </div>
      )}

      {/* Findings grouped by severity */}
      {grouped.map(group => {
        const config = SEVERITY_CONFIG[group.severity] ?? SEVERITY_CONFIG.info;
        return (
          <div key={group.severity} style={{ marginBottom: "1.5rem" }}>
            <h3 style={{
              fontSize: "var(--text-sm)",
              fontWeight: 600,
              color: config.text,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              marginBottom: 8,
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}>
              {config.icon} {config.label} ({group.items.length})
            </h3>

            {group.items.map(finding => {
              const isExpanded = expanded.has(finding.id);
              const isResolving = resolving.has(finding.id);
              const isFixing = fixing.has(finding.id);

              return (
                <div
                  key={finding.id}
                  style={{
                    background: finding.resolved ? "var(--bg-card)" : config.bg,
                    border: `1px solid ${finding.resolved ? "var(--border)" : config.border}`,
                    borderRadius: 8,
                    marginBottom: 8,
                    opacity: finding.resolved ? 0.6 : 1,
                    transition: "opacity 0.2s",
                  }}
                >
                  {/* Finding header */}
                  <div
                    onClick={() => toggle(finding.id)}
                    style={{
                      padding: "0.75rem 1rem",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 12,
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flex: 1, minWidth: 0 }}>
                      {finding.review_engine && (
                        <span style={{
                          fontSize: "var(--text-xs, 0.75rem)",
                          background: finding.review_engine === 'manus'
                            ? "rgba(245, 158, 11, 0.15)" : "rgba(168, 85, 247, 0.15)",
                          borderRadius: 4,
                          padding: "2px 6px",
                          color: finding.review_engine === 'manus'
                            ? "#f59e0b" : "#a855f7",
                          whiteSpace: "nowrap",
                          fontWeight: 600,
                        }}>
                          {finding.review_engine === 'manus' ? 'DEEP' : 'QUICK'}
                        </span>
                      )}
                      <span style={{
                        fontSize: "var(--text-xs, 0.75rem)",
                        background: "rgba(0,0,0,0.1)",
                        borderRadius: 4,
                        padding: "2px 6px",
                        color: "var(--text-muted)",
                        whiteSpace: "nowrap",
                      }}>
                        {CATEGORY_LABELS[finding.category] ?? finding.category}
                      </span>
                      <span style={{
                        fontWeight: 500,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}>
                        {finding.title}
                      </span>
                      {finding.resolved && (
                        <span style={{
                          fontSize: "var(--text-xs, 0.75rem)",
                          color: "#22c55e",
                          fontWeight: 600,
                        }}>
                          ✓ Resolved
                        </span>
                      )}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      {finding.auto_fixable && !finding.resolved && (
                        <span style={{
                          fontSize: "var(--text-xs, 0.75rem)",
                          background: "rgba(34, 197, 94, 0.15)",
                          color: "#22c55e",
                          borderRadius: 4,
                          padding: "2px 6px",
                          whiteSpace: "nowrap",
                        }}>
                          Auto-fixable
                        </span>
                      )}
                      {!finding.resolved && !isCoverageFinding(finding) && (
                        <button
                          onClick={(e) => { e.stopPropagation(); setConfirmFinding(finding); }}
                          disabled={isFixing}
                          title="Fix this finding"
                          style={{
                            padding: "4px 10px",
                            borderRadius: 6,
                            border: "none",
                            background: isFixing ? "var(--text-muted)" : "var(--orange, #00E5FF)",
                            color: "#fff",
                            cursor: isFixing ? "wait" : "pointer",
                            fontSize: "var(--text-xs, 0.75rem)",
                            fontWeight: 600,
                            whiteSpace: "nowrap",
                            opacity: isFixing ? 0.7 : 1,
                          }}
                        >
                          {isFixing ? "⏳" : "🛠️ Fix"}
                        </button>
                      )}
                      {!finding.resolved && isCoverageFinding(finding) && (
                        <span style={{
                          fontSize: "var(--text-xs, 0.75rem)",
                          color: "var(--text-muted)",
                          whiteSpace: "nowrap",
                          fontStyle: "italic",
                        }}>
                          Requires spec
                        </span>
                      )}
                      <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
                        {isExpanded ? "▲" : "▼"}
                      </span>
                    </div>
                  </div>

                  {/* Expanded details */}
                  {isExpanded && (
                    <div style={{
                      padding: "0 1rem 1rem",
                      borderTop: `1px solid ${finding.resolved ? "var(--border)" : config.border}`,
                    }}>
                      <p style={{ margin: "0.75rem 0 0.5rem", fontSize: "var(--text-sm)", lineHeight: 1.6 }}>
                        {finding.description}
                      </p>

                      {finding.suggestion && (
                        <div style={{
                          background: "rgba(59, 130, 246, 0.08)",
                          border: "1px solid rgba(59, 130, 246, 0.2)",
                          borderRadius: 6,
                          padding: "0.5rem 0.75rem",
                          marginTop: 8,
                          fontSize: "var(--text-sm)",
                        }}>
                          <strong>Suggestion:</strong> {finding.suggestion}
                        </div>
                      )}

                      {finding.file_path && (
                        <p style={{ fontSize: "var(--text-xs, 0.75rem)", color: "var(--text-muted)", marginTop: 8 }}>
                          📁 {finding.file_path}
                        </p>
                      )}

                      {!finding.resolved && !isCoverageFinding(finding) && (
                        <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
                          <button
                            onClick={(e) => { e.stopPropagation(); setConfirmFinding(finding); }}
                            disabled={isFixing}
                            style={{
                              padding: "0.375rem 0.75rem",
                              borderRadius: 6,
                              border: "none",
                              background: isFixing ? "var(--text-muted)" : "var(--orange, #00E5FF)",
                              color: "#fff",
                              cursor: isFixing ? "wait" : "pointer",
                              fontSize: "var(--text-sm)",
                              fontWeight: 600,
                              display: "flex",
                              alignItems: "center",
                              gap: 6,
                              transition: "opacity 0.15s",
                              opacity: isFixing ? 0.7 : 1,
                            }}
                            onMouseEnter={(e) => !isFixing && (e.currentTarget.style.opacity = '0.85')}
                            onMouseLeave={(e) => !isFixing && (e.currentTarget.style.opacity = '1')}
                          >
                            {isFixing ? "⏳ Working on it..." : "🛠️ Fix it"}
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleResolve(finding.id); }}
                            disabled={isResolving}
                            style={{
                              padding: "0.375rem 0.75rem",
                              borderRadius: 6,
                              border: "1px solid var(--border)",
                              background: "var(--bg-card)",
                              color: "var(--text)",
                              cursor: isResolving ? "wait" : "pointer",
                              fontSize: "var(--text-sm)",
                              opacity: isResolving ? 0.5 : 1,
                            }}
                          >
                            {isResolving ? "Resolving..." : "Mark Resolved"}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        );
      })}
      {/* Fix Confirmation Modal */}
      {confirmFinding && (
        <div
          style={{
            position: "fixed", inset: 0, zIndex: 100,
            background: "rgba(0,0,0,0.5)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}
          onClick={() => setConfirmFinding(null)}
        >
          <div
            style={{
              background: "var(--surface, #1a1a2e)", border: "1px solid var(--border)",
              borderRadius: 8, padding: "24px", maxWidth: 480, width: "90%",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: "0 0 12px", fontSize: "var(--text-base)", fontWeight: 600, color: "var(--text)" }}>
              Fix: {confirmFinding.title}
            </h3>
            <p style={{ margin: "0 0 8px", fontSize: "var(--text-sm)", color: "var(--text-muted)", lineHeight: 1.5 }}>
              This will: {fixDescription(confirmFinding)}
            </p>
            <p style={{ margin: "0 0 20px", fontSize: "var(--text-xs)", color: "var(--text-muted)", fontFamily: "var(--font-mono)" }}>
              Estimated credits: ~{estimateFixCredits(confirmFinding)}
            </p>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button
                onClick={() => setConfirmFinding(null)}
                style={{
                  padding: "8px 16px", borderRadius: 6,
                  border: "1px solid var(--border)", background: "transparent",
                  color: "var(--text-muted)", cursor: "pointer", fontSize: "var(--text-sm)",
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  const f = confirmFinding;
                  setConfirmFinding(null);
                  handleFixIt(f);
                }}
                style={{
                  padding: "8px 16px", borderRadius: 6, border: "none",
                  background: "var(--orange, #00E5FF)", color: "#fff",
                  cursor: "pointer", fontSize: "var(--text-sm)", fontWeight: 600,
                }}
              >
                Confirm Fix →
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
